/* eslint-disable no-unused-vars */
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import createBase from '../utils/d3-base';
import boundariesData from '../../assets/geo/us.json';
// source: https://hub.arcgis.com/datasets/esri::usa-major-cities/explore?location=33.316003%2C-95.689388%2C4.51
import citiesData from '../../assets/geo/cities.json';
import providersData from '../../assets/geo/abortion_providers.json';
import urbanData from '../../assets/geo/urban_areas.json';
import data from '../../../data/data.json';
import { swoopyDrag } from './swoopy-drag';
import { wordwrap } from 'd3-jetpack';

// a reference to the default graphic container, change if needed
const container = d3.select('#graphic');

// a helper function to clear the container of its contents
const clearContainer = () => container.html('');

// a helper function to grab the container's width
const getFrameWidth = () => container.node().offsetWidth;

// import data by getting the window variable, OR by importing the filepath
// let data = window.DATA;
// import data from '../../../data/data.json';
// import data from 'Data/data.json';

/**
 * This function is called to render a graphic, using d3 or a library of your choice.
 *
 * @return {void}
 */
export default function renderGraphic() {
  // pass the recalculated frameWidth to parts of your chart (like an axis) that change with resize!
  clearContainer();
  const frameWidth = getFrameWidth();

  // data variables
  let abortionData = data['abortions restrictions by state chart'];
  let listOfStates = abortionData.map(d => d.state_code);
  let triggerStates = abortionData
    .filter(d => d.trigger_ban == true)
    .map(d => d.state_code);

  // just get the states we want
  boundariesData.features = boundariesData.features.filter(d =>
    listOfStates.includes(d.id)
  );
  let boundariesDataTrigger = {
    type: 'FeatureCollection',
    features: boundariesData.features.filter(d => triggerStates.includes(d.id)),
  };

  // source: https://makeshiftinsights.com/blog/basic-maps-with-d3/
  // Setting up the svg element for D3 to draw in
  let breakPoint = 479,
    width = frameWidth,
    height = frameWidth > breakPoint ? 450 : 300;
  let margin = {
    top: 5,
    bottom: 5,
    right: 20,
    left: 20,
  };

  let svg = container
    .append('svg')
    .attr('width', width)
    .attr('height', height);

  // A projection tells D3 how to orient the GeoJSON features
  let usaProjection = d3.geoAlbersUsa().fitExtent(
    [
      [50, 10],
      [width - margin.right - margin.left, height - margin.top - margin.bottom],
    ],
    boundariesData
  );

  // The path generator uses the projection to convert the GeoJSON
  // geometry to a set of coordinates that D3 can understand
  let pathGenerator = d3.geoPath().projection(usaProjection);

  let diagonalStripePattern = svg
    .append('defs')
    .append('pattern')
    .attr('id', 'diagonal-stripe')
    .attr('patternUnits', 'userSpaceOnUse')
    .attr('patternTransform', 'rotate(45 0 0)')
    .attr('width', 4)
    .attr('height', 4);

  diagonalStripePattern
    .append('rect')
    .attr('fill', 'none')
    .attr('width', 20)
    .attr('height', 20);

  diagonalStripePattern
    .append('rect')
    .attr('width', 1.5)
    .attr('height', 6)
    .attr('transform', 'translate(0,0)')
    .attr('fill', 'rgba(255, 255, 255, 0.35)');

  // Tell D3 to render a path for each GeoJSON feature
  svg
    .append('clipPath') // define a clip path
    .attr('id', 'map-clip')
    .selectAll('path')
    .data(boundariesData.features)
    .enter()
    .append('path')
    .attr('d', pathGenerator); // This is where the magic happens

  let paths = svg
    .selectAll('.states')
    .data(boundariesData.features)
    .enter()
    .append('path')
    .attr('d', pathGenerator) // This is where the magic happens
    .attr('stroke', d => {
      return '#d0d0d0';
    }) // Color of the lines themselves
    .attr('stroke-width', d => {
      return 1;
    }) // Color of the lines themselves
    .attr('fill', '#f3f3f3')
    .attr('class', 'states');

  let pathsUrban = svg
    .selectAll('.urban')
    .data(urbanData.features)
    .enter()
    .append('path')
    .attr('d', pathGenerator) // This is where the magic happens
    .attr('class', 'urban')
    .attr('fill', '#c6c6c6')
    .attr('opacity', 0.75)
    .attr('stroke', '#c6c6c6')
    .attr('stroke-width', '0.75px')
    .style('stroke-linejoin', 'round')
    .style('stroke-miterlimit', 2)
    .style('stroke-linecap', 'round');

  pathsUrban.attr('clip-path', 'url(#map-clip)');

  let providerPoints = svg
    .selectAll('.providers-point')
    .data(providersData.features)
    .enter()
    .append('circle')
    .attr('class', 'providers-point')
    .attr('transform', function(d) {
      if (d.geometry != null) {
        return 'translate(' + usaProjection(d.geometry.coordinates) + ')';
      }
    })
    .attr('r', 3)
    .attr('fill', 'none')
    .attr('stroke', '#c66390')
    .attr('stroke-width', 1.5);

  // set up the clinic labels
  let text = svg
    .selectAll('.number-clinic-label')
    .data(boundariesData.features)
    .enter()
    .append('text')
    .attr('d', pathGenerator)
    .attr('transform', function(d) {
      return 'translate(' + pathGenerator.centroid(d) + ')';
    })
    .text(function(d) {
      let number = abortionData.find(a => a.state_code == d.id).number_clinics;
      return `${number} clinic${number > 1 ? 's' : ''}`;
    })
    .attr('dy', function(d) {
      if ([''].includes(d.properties.name))
        return frameWidth > breakPoint ? '2.5em' : '1.5em';

      if (['Arkansas'].includes(d.properties.name)) return '-0.5em';
      if (['Texas'].includes(d.properties.name)) return '1em';
    })
    .attr('dx', function(d) {
      return (
        -(
          d3
            .select(this)
            .node()
            .getBBox().width / 2
        ) +
        5 +
        (d.properties.name == 'Louisiana' ? -15 : 0)
      );
    })
    .attr('font-style', 'italic')
    .attr('font-size', '12px')
    .attr('font-weight', 500)
    .attr('fill', '#222')
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 2)
    .attr('paint-order', 'stroke fill')
    .attr('class', 'number-clinic-label');
  // rest of your code goes here
  // use createBase() to create the base of an d3 chart
}
